<!-- 商家端 - 本店客服 -->
<template>
  <div class="shop-kefu-page">
    <ContentWrap title="本店客服" class="shop-kefu-wrap" :body-style="wrapBodyStyle">
      <el-container class="shop-kefu" v-loading="loading">
        <el-aside width="280px" class="shop-kefu__aside">
        <div class="shop-kefu__aside-title">会话 ({{ conversations.length }})</div>
        <div
          v-for="item in conversations"
          :key="item.id"
          class="shop-kefu__conv"
          :class="{ active: item.id === current?.id }"
          @click="selectConversation(item)"
        >
          <el-badge :hidden="!item.adminUnreadMessageCount" :value="item.adminUnreadMessageCount" :max="99">
            <el-avatar :src="item.userAvatar" :size="40" />
          </el-badge>
          <div class="shop-kefu__conv-meta">
            <div class="shop-kefu__conv-name">
              {{ item.userNickname || '用户' }}
              <el-tag
                v-if="item.csBotStatus === 'HUMAN'"
                size="small"
                type="warning"
                effect="plain"
                class="ml-6px"
              >
                待人工
              </el-tag>
              <el-tag
                v-else-if="item.csBotStatus === 'BOT' || !item.csBotStatus"
                size="small"
                type="info"
                effect="plain"
                class="ml-6px"
              >
                AI
              </el-tag>
            </div>
            <div class="shop-kefu__conv-last">{{ previewText(item) }}</div>
          </div>
        </div>
        <el-empty v-if="!conversations.length" description="暂无会话" :image-size="64" />
      </el-aside>

      <el-main class="shop-kefu__main">
        <template v-if="current">
          <div class="shop-kefu__header">
            与 {{ current.userNickname || '用户' }} 的对话
            <el-tag
              v-if="current.csBotStatus === 'HUMAN'"
              size="small"
              type="warning"
              class="ml-8px"
            >
              待人工 / 人工中
            </el-tag>
            <el-tag v-else size="small" type="info" class="ml-8px">AI 接待中</el-tag>
          </div>
          <div ref="msgBoxRef" class="shop-kefu__msgs">
            <div
              v-for="msg in messages"
              :key="msg.id"
              class="shop-kefu__msg-row"
              :class="{ mine: msg.senderType !== 1 }"
            >
              <el-avatar
                v-if="msg.senderType === 1"
                :src="msg.senderAvatar || current.userAvatar || undefined"
                :size="36"
              />
              <div class="shop-kefu__msg">
                <!-- 文本 -->
                <div v-if="msgKind(msg) === 'text'" class="shop-kefu__bubble">
                  {{ displayText(msg) }}
                </div>
                <!-- 图片 -->
                <div v-else-if="msgKind(msg) === 'image'" class="shop-kefu__card">
                  <el-image
                    :src="parseContent(msg).picUrl"
                    :preview-src-list="[parseContent(msg).picUrl]"
                    fit="cover"
                    class="shop-kefu__img"
                    preview-teleported
                  />
                </div>
                <!-- 商品卡片 -->
                <div v-else-if="msgKind(msg) === 'product'" class="shop-kefu__card shop-kefu__product">
                  <el-image
                    :src="parseContent(msg).picUrl"
                    fit="cover"
                    class="shop-kefu__product-pic"
                  />
                  <div class="shop-kefu__product-body">
                    <div class="shop-kefu__product-name">{{ parseContent(msg).spuName || '商品' }}</div>
                    <div class="shop-kefu__product-meta">
                      <span v-if="parseContent(msg).stock != null">库存 {{ parseContent(msg).stock }}</span>
                      <span v-if="parseContent(msg).salesCount != null">
                        销量 {{ parseContent(msg).salesCount }}
                      </span>
                    </div>
                    <div class="shop-kefu__product-price">
                      ¥{{ fenToYuan(parseContent(msg).price) }}
                    </div>
                  </div>
                </div>
                <!-- 订单卡片 -->
                <div v-else-if="msgKind(msg) === 'order'" class="shop-kefu__card shop-kefu__order">
                  <div class="shop-kefu__order-head">
                    <span class="shop-kefu__order-no">订单号 {{ parseContent(msg).no || '—' }}</span>
                    <span class="shop-kefu__order-status">
                      {{ parseContent(msg).statusText || orderStatusText(parseContent(msg)) }}
                    </span>
                  </div>
                  <div class="shop-kefu__order-row">
                    <el-image
                      v-if="orderPic(parseContent(msg))"
                      :src="orderPic(parseContent(msg))"
                      fit="cover"
                      class="shop-kefu__order-pic"
                    />
                    <div class="shop-kefu__order-info">
                      <div class="shop-kefu__product-name">{{ orderTitle(parseContent(msg)) }}</div>
                      <div class="shop-kefu__product-price">
                        ¥{{ fenToYuan(parseContent(msg).payPrice) }}
                      </div>
                    </div>
                  </div>
                  <el-button
                    v-if="parseContent(msg).id"
                    link
                    type="primary"
                    size="small"
                    class="mt-6px"
                    @click="goOrder"
                  >
                    去订单中心处理
                  </el-button>
                </div>
                <div v-else class="shop-kefu__bubble">{{ displayText(msg) }}</div>
                <div class="shop-kefu__time">{{ formatTime(msg.createTime) }}</div>
              </div>
              <el-avatar
                v-if="msg.senderType !== 1"
                :src="msg.senderAvatar || shopLogo || undefined"
                :size="36"
              />
            </div>
          </div>
          <div class="shop-kefu__input">
            <el-input
              v-model="draft"
              type="textarea"
              :rows="3"
              maxlength="500"
              show-word-limit
              placeholder="输入回复内容"
              @keydown.enter.exact.prevent="sendText"
            />
            <el-button type="primary" :loading="sending" @click="sendText">发送</el-button>
          </div>
        </template>
        <el-empty v-else description="请选择左侧会话" />
      </el-main>
      </el-container>
    </ContentWrap>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import { MerchantKeFuConversationApi, MerchantKeFuMessageApi } from '@/api/merchant/kefu'
import * as MerchantShopApi from '@/api/merchant/shop'
import { getMerchantRefreshToken } from '@/utils/auth'
import { useWebSocket } from '@vueuse/core'
import { formatDate } from '@/utils/formatTime'
import { fenToYuan } from '@/utils'
import { storePath } from '@/views/merchant/registry/paths'
import { KeFuMessageContentTypeEnum } from '@/views/mall/promotion/kefu/components/tools/constants'

defineOptions({ name: 'MerchantShopKefu' })

/** ContentWrap 卡片 body：让客服面板吃满剩余高度，输入框贴底 */
const wrapBodyStyle = {
  padding: '10px',
  overflow: 'hidden',
  flex: '1',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '0'
} as const

const message = useMessage()
const router = useRouter()
const loading = ref(false)
const sending = ref(false)
const conversations = ref<any[]>([])
const current = ref<any>(null)
const messages = ref<any[]>([])
const draft = ref('')
const msgBoxRef = ref<HTMLElement>()
const shopLogo = ref('')

const WS_TYPE_MSG = 'kefu_message_type'
const WS_TYPE_READ = 'kefu_message_read_status_change'

function parseContent(msg: any): any {
  const raw = msg?.content
  if (raw == null) return {}
  if (typeof raw === 'object') return raw
  try {
    return JSON.parse(raw)
  } catch {
    return { text: String(raw) }
  }
}

/** 1 文本 / 2 图片 / 10 商品 / 11 订单；兼容错误类型码时按内容推断 */
function msgKind(msg: any): 'text' | 'image' | 'product' | 'order' | 'other' {
  const t = Number(msg?.contentType)
  if (t === KeFuMessageContentTypeEnum.IMAGE) return 'image'
  if (t === KeFuMessageContentTypeEnum.PRODUCT) return 'product'
  if (t === KeFuMessageContentTypeEnum.ORDER) return 'order'
  // 旧商家端误用 3/4
  if (t === 3) return 'product'
  if (t === 4) return 'order'
  const c = parseContent(msg)
  if (c?.spuName && (c?.spuId != null || c?.price != null)) return 'product'
  if (c?.no && (c?.payPrice != null || c?.status != null || c?.items)) return 'order'
  if (c?.picUrl && !c?.text && t === KeFuMessageContentTypeEnum.TEXT) return 'image'
  if (t === KeFuMessageContentTypeEnum.TEXT || c?.text != null) return 'text'
  return 'other'
}

function displayText(msg: any) {
  const c = parseContent(msg)
  return c?.text || (typeof msg.content === 'string' && !msg.content.startsWith('{') ? msg.content : '') || '[消息]'
}

function previewText(item: any) {
  const fake = { content: item.lastMessageContent, contentType: item.lastMessageContentType }
  const kind = msgKind(fake)
  if (kind === 'image') return '[图片]'
  if (kind === 'product') return '[商品] ' + (parseContent(fake).spuName || '')
  if (kind === 'order') return '[订单] ' + (parseContent(fake).no || '')
  return displayText(fake)
}

function orderStatusText(order: any) {
  if (!order) return ''
  if (order.statusText) return order.statusText
  if (order.status === 0) return '待付款'
  if (order.status === 10) return '待发货'
  if (order.status === 20) return '待收货'
  if (order.status === 30) return '已完成'
  if (order.status === 40) return '已取消'
  return ''
}

function orderPic(order: any) {
  return order?.picUrl || order?.items?.[0]?.picUrl || ''
}

function orderTitle(order: any) {
  return order?.spuName || order?.items?.[0]?.spuName || '订单商品'
}

function goOrder() {
  router.push(storePath('/order'))
}

function formatTime(t: any) {
  return t ? formatDate(t) : ''
}

async function loadConversations() {
  loading.value = true
  try {
    conversations.value = (await MerchantKeFuConversationApi.getConversationList()) || []
    // 有会话且当前未选 / 当前会话已不在列表中 → 自动选第一条
    const stillThere =
      current.value?.id && conversations.value.some((c) => c.id === current.value.id)
    if (conversations.value.length && !stillThere) {
      await selectConversation(conversations.value[0])
    }
    if (!conversations.value.length) {
      current.value = null
      messages.value = []
    }
  } finally {
    loading.value = false
  }
}

async function selectConversation(item: any) {
  if (!item?.id) {
    current.value = null
    messages.value = []
    return
  }
  current.value = item
  try {
    const list = await MerchantKeFuMessageApi.getKeFuMessageList({
      conversationId: item.id,
      limit: 50
    })
    messages.value = (list || []).slice().reverse()
    await MerchantKeFuMessageApi.updateKeFuMessageReadStatus(item.id)
    item.adminUnreadMessageCount = 0
  } catch (e) {
    console.error(e)
    message.error('加载会话消息失败')
    messages.value = []
  }
  await nextTick()
  scrollBottom()
}

function scrollBottom() {
  const el = msgBoxRef.value
  if (el) el.scrollTop = el.scrollHeight
}

async function sendText() {
  const text = draft.value.trim()
  if (!text || !current.value) return
  sending.value = true
  try {
    await MerchantKeFuMessageApi.sendKeFuMessage({
      conversationId: current.value.id,
      contentType: 1,
      content: JSON.stringify({ text })
    })
    draft.value = ''
    await selectConversation(current.value)
    await loadConversations()
  } finally {
    sending.value = false
  }
}

// WebSocket：商家端；按 shopId 过滤（本页只展示本店，服务端已只推 MERCHANT）
const wsUrl = ref(
  (import.meta.env.VITE_BASE_URL + '/infra/ws').replace('http', 'ws') + '?token=' + getMerchantRefreshToken()
)
const { data: wsData, open: openWs, close: closeWs } = useWebSocket(wsUrl.value, {
  autoReconnect: true,
  heartbeat: true
})

watch(
  () => wsData.value,
  async (raw) => {
    if (!raw || raw === 'pong') return
    try {
      const frame = JSON.parse(raw)
      if (frame.type === WS_TYPE_MSG) {
        const payload = typeof frame.content === 'string' ? JSON.parse(frame.content) : frame.content
        // 只处理本店会话（shopId>0）；若当前打开则追加
        if (payload?.shopId != null && Number(payload.shopId) === 0) return
        await loadConversations()
        if (current.value && payload?.conversationId === current.value.id) {
          if (!messages.value.some((m) => m.id === payload.id)) {
            messages.value.push(payload)
            await nextTick()
            scrollBottom()
          }
          await MerchantKeFuMessageApi.updateKeFuMessageReadStatus(current.value.id)
        }
      } else if (frame.type === WS_TYPE_READ) {
        await loadConversations()
      }
    } catch (e) {
      console.error(e)
    }
  }
)

onMounted(async () => {
  try {
    const shop = await MerchantShopApi.getShopInfo()
    shopLogo.value = shop?.shopLogo || ''
  } catch {
    shopLogo.value = ''
  }
  await loadConversations()
  openWs()
})
onUnmounted(() => closeWs())
</script>

<style scoped lang="scss">
/* 顶栏 50 + 内容区上下 padding 32；铺满视口，避免输入框下方大块空白 */
.shop-kefu-page {
  height: calc(100vh - 82px);
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.shop-kefu-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-bottom: 0 !important;
  :deep(.el-card__body) {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }
}
.shop-kefu {
  flex: 1;
  height: 100%;
  min-height: 0;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 8px;
  overflow: hidden;
  background: var(--el-bg-color);
}
.shop-kefu__aside {
  border-right: 1px solid var(--el-border-color-lighter);
  padding: 12px;
  overflow-y: auto;
  height: 100%;
}
.shop-kefu__aside-title {
  font-weight: 600;
  margin-bottom: 12px;
  color: #666;
}
.shop-kefu__conv {
  display: flex;
  gap: 10px;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  &.active,
  &:hover {
    background: var(--el-fill-color-light);
  }
}
.shop-kefu__conv-meta {
  flex: 1;
  min-width: 0;
}
.shop-kefu__conv-name {
  font-weight: 500;
}
.shop-kefu__conv-last {
  font-size: 12px;
  color: #999;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.shop-kefu__main {
  display: flex;
  flex-direction: column;
  padding: 0 !important;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
.shop-kefu__header {
  flex-shrink: 0;
  padding: 12px 16px;
  border-bottom: 1px solid var(--el-border-color-lighter);
  font-weight: 600;
}
.shop-kefu__msgs {
  flex: 1;
  min-height: 0;
  padding: 16px;
  overflow-y: auto;
  background: #f7f8fa;
}
.shop-kefu__msg-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
  &.mine {
    justify-content: flex-end;
    .shop-kefu__msg {
      text-align: right;
    }
    .shop-kefu__bubble {
      background: var(--el-color-primary);
      color: #fff;
    }
  }
}
.shop-kefu__msg {
  max-width: 70%;
}
.shop-kefu__bubble {
  display: inline-block;
  padding: 8px 12px;
  border-radius: 8px;
  background: #fff;
  word-break: break-all;
  text-align: left;
}
.shop-kefu__card {
  display: inline-block;
  max-width: 320px;
  padding: 10px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid var(--el-border-color-lighter);
  text-align: left;
}
.shop-kefu__img {
  width: 180px;
  height: 180px;
  border-radius: 8px;
}
.shop-kefu__product {
  display: flex;
  gap: 10px;
  align-items: stretch;
}
.shop-kefu__product-pic {
  width: 72px;
  height: 72px;
  border-radius: 8px;
  flex-shrink: 0;
}
.shop-kefu__product-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.shop-kefu__product-name {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.shop-kefu__product-meta {
  font-size: 12px;
  color: #999;
  display: flex;
  gap: 10px;
  margin-top: 4px;
}
.shop-kefu__product-price {
  margin-top: 4px;
  color: #ff3000;
  font-weight: 600;
}
.shop-kefu__order-head {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  margin-bottom: 8px;
}
.shop-kefu__order-no {
  color: #666;
}
.shop-kefu__order-status {
  color: #e6a23c;
  font-weight: 600;
}
.shop-kefu__order-row {
  display: flex;
  gap: 10px;
}
.shop-kefu__order-pic {
  width: 56px;
  height: 56px;
  border-radius: 6px;
  flex-shrink: 0;
}
.shop-kefu__order-info {
  flex: 1;
  min-width: 0;
}
.shop-kefu__time {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}
.shop-kefu__input {
  flex-shrink: 0;
  display: flex;
  gap: 12px;
  align-items: flex-end;
  padding: 12px 16px;
  border-top: 1px solid var(--el-border-color-lighter);
  background: var(--el-bg-color);
}
.mt-6px {
  margin-top: 6px;
}
</style>
